import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useApi } from "../hooks/useApi";
import useRBAC from "../hooks/useRBAC";
import { formatUtcDateTime } from "../utils/dateTime";
import NercApprovalCard from "./NercApprovalCard";
import NercCsvImportModal from "./NercCsvImportModal";
import NercSiteDetails from "./NercSiteDetails";
import { RetireForm, SiteForm } from "./NercAssetForms";
import { IMPACTS, IMPACT_TONES, SITE_TYPES, apiError, saveBlob, siteTypeLabel } from "../utils/nercAssets";
import NercPatchPanel from "./NercPatchPanel";
import { useNercPatchManagement } from "../contexts/FeatureFlagsContext";
import { SegmentedTabs } from "./TabStrips";
import { btn } from "../utils/buttonStyles";
import { Boxes, Download, Plus, ShieldCheck, Upload } from "lucide-react";

/**
 * GRC → Assets → NERC sites (point 3): the CIP-002 inventory — sites, their BES Cyber Systems
 * (High / Medium / Low, with the Attachment 1 criterion), Electronic Security Perimeters and
 * Cyber Assets (BCA / EACMS / PACS / PCA) — with CSV import / export and the CIP-002 R2 review and
 * approval. An inventory: nothing here is a control, a risk or a readiness input.
 * Reads for every org member; changes for users who manage Security Insights (the backend checks).
 */

// accent: the card's left edge, coloured when the number needs attention.
const Stat = ({ label, value, tone = "text-gray-900", accent = "border-l-indigo-300", title }) => (
  <div className={`rounded-lg border border-gray-200 border-l-4 ${accent} bg-gradient-to-br from-white to-gray-50 px-3 py-2 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all`} title={title}>
    <div className="text-xs font-medium text-gray-500">{label}</div>
    <div className={`text-2xl font-bold ${tone}`}>{value}</div>
  </div>
);

/**
 * The inventory, or (Features:NercPatchManagement) its CIP-007 R2 patch management, built on it.
 */
export default function NercSitesPanel({ organizationId }) {
  const patchManagement = useNercPatchManagement();
  const [section, setSection] = useState("inventory");
  const current = patchManagement ? section : "inventory";
  return (
    <div className="space-y-4">
      {patchManagement && (
        <SegmentedTabs ariaLabel="NERC section" value={current} onChange={setSection} items={[
          { key: "inventory", label: "Inventory", icon: Boxes, testId: "nerc-section-inventory" },
          { key: "patches", label: "Patch management", icon: ShieldCheck, testId: "nerc-section-patches" },
        ]} />
      )}
      {current === "inventory" ? <NercInventory organizationId={organizationId} /> : <NercPatchPanel organizationId={organizationId} />}
    </div>
  );
}

function NercInventory({ organizationId }) {
  const apiCall = useApi();
  const { canManageSecurityInsights } = useRBAC();
  const canManage = canManageSecurityInsights(organizationId);

  const [data, setData] = useState(null);
  const [status, setStatus] = useState(null);
  const [users, setUsers] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [includeRetired, setIncludeRetired] = useState(false);
  const [search, setSearch] = useState("");
  const [impact, setImpact] = useState("");
  const [siteType, setSiteType] = useState("");
  const [needsEspOnly, setNeedsEspOnly] = useState(false);
  const [openSite, setOpenSite] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [sites, cip002] = await Promise.all([
        apiCall("GET", `/nerc-assets/sites?includeRetired=${includeRetired ? "true" : "false"}`),
        apiCall("GET", "/nerc-assets/cip002"),
      ]);
      setData(sites); setStatus(cip002);
    } catch (err) {
      setError(apiError(err, "Could not load the NERC inventory."));
    } finally { setLoading(false); }
  }, [apiCall, includeRetired]);

  useEffect(() => { load(); }, [load, organizationId]);

  useEffect(() => {
    if (!canManage) return;
    apiCall("GET", "/users").then((res) => setUsers(res || [])).catch(() => setUsers([]));
  }, [apiCall, canManage]);

  const sites = useMemo(() => (data?.sites || []).filter((s) =>
    (!search || s.name.toLowerCase().includes(search.toLowerCase()) || (s.location || "").toLowerCase().includes(search.toLowerCase()))
    && (!impact || (impact === "none" ? !s.impact : s.impact === impact))
    && (!siteType || s.siteType === siteType)
    && (!needsEspOnly || s.needsEsp > 0)), [data, search, impact, siteType, needsEspOnly]);

  const exportCsv = async () => {
    try {
      const blob = await apiCall("GET", "/nerc-assets/export", null, false, "blob");
      if (blob) saveBlob(blob, `nerc-cip-002-inventory-${new Date().toISOString().slice(0, 10)}.csv`);
    } catch (err) { setError(apiError(err, "Could not export the inventory.")); }
  };

  const saved = () => { setDialog(null); load(); };
  const totals = data?.totals;

  return (
    <div className="space-y-4" data-testid="nerc-sites-panel">
      <p className="text-xs text-gray-500">
        Your CIP-002 identifications: sites, BES Cyber Systems with their impact rating and Attachment 1 criterion, Electronic Security
        Perimeters and Cyber Assets. An inventory only: it never changes readiness or creates risks.
      </p>

      {totals && (
        <div className="grid grid-cols-2 md:grid-cols-6 gap-2" data-testid="nerc-totals">
          <Stat label="Sites" value={totals.sites} />
          <Stat label="High impact systems" value={totals.highSystems} tone={totals.highSystems ? "text-red-700" : "text-gray-900"}
            accent={totals.highSystems ? "border-l-red-500" : undefined} />
          <Stat label="Medium impact systems" value={totals.mediumSystems} tone={totals.mediumSystems ? "text-orange-700" : "text-gray-900"}
            accent={totals.mediumSystems ? "border-l-orange-400" : undefined} />
          <Stat label="Low impact systems" value={totals.lowSystems} />
          <Stat label="Cyber Assets" value={totals.cyberAssets}
            title={Object.entries(totals.cyberAssetsByClass || {}).map(([k, v]) => `${k}: ${v}`).join(" · ")} />
          <Stat label="Need an ESP" value={totals.needsEsp} tone={totals.needsEsp ? "text-red-700" : "text-gray-900"}
            accent={totals.needsEsp ? "border-l-red-500" : "border-l-green-400"}
            title="High or Medium impact BCAs and PCAs with routable connectivity and no Electronic Security Perimeter (CIP-005 R1.1)" />
        </div>
      )}

      <NercApprovalCard status={status} canManage={canManage} users={users} onChanged={load} />

      <div className="flex flex-wrap items-center gap-2">
        <input className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" placeholder="Search sites" value={search} onChange={(e) => setSearch(e.target.value)} />
        <select className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={impact} onChange={(e) => setImpact(e.target.value)} aria-label="Impact">
          <option value="">All impacts</option>
          {IMPACTS.map((i) => <option key={i} value={i}>{i}</option>)}
          <option value="none">Not categorised</option>
        </select>
        <select className="border border-gray-300 rounded-lg px-2 py-1.5 text-sm" value={siteType} onChange={(e) => setSiteType(e.target.value)} aria-label="Site type">
          <option value="">All site types</option>
          {SITE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={needsEspOnly} onChange={(e) => setNeedsEspOnly(e.target.checked)} /> Needs ESP</label>
        <label className="flex items-center gap-1 text-sm"><input type="checkbox" checked={includeRetired} onChange={(e) => setIncludeRetired(e.target.checked)} /> Show retired</label>
        <div className="flex-1" />
        <button type="button" onClick={exportCsv} className={btn("neutral")}><Download className="h-4 w-4" />Export CSV</button>
        {canManage && <button type="button" onClick={() => setDialog({ kind: "import" })} className={btn("secondary")}><Upload className="h-4 w-4" />Import CSV</button>}
        {canManage && <button type="button" onClick={() => setDialog({ kind: "site" })} className={btn("primary")}><Plus className="h-4 w-4" />Add site</button>}
      </div>

      {notice && <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg px-3 py-2" data-testid="nerc-notice">{notice}</p>}
      {error && <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">{error}</p>}
      {loading && !data && <p className="text-sm text-gray-500">Loading…</p>}

      {data && data.sites.length === 0 && (
        <div className="text-sm text-gray-600 bg-white border border-dashed border-gray-300 rounded-lg p-6 text-center" data-testid="nerc-empty">
          No sites yet. {canManage ? "Add a site, or import your inventory from a CSV file." : "Someone who manages Security Insights can add them."}
        </div>
      )}

      {data && data.sites.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm" data-testid="nerc-sites-table">
            <thead className="bg-gray-50">
              <tr className="text-left text-xs text-gray-500">
                <th className="px-4 py-2">Site</th><th>Type</th><th>Impact</th><th>Systems (H / M / L)</th><th>Cyber Assets</th><th>Needs ESP</th><th>Owner</th><th>Last change</th><th />
              </tr>
            </thead>
            <tbody>
              {sites.length === 0 && <tr><td colSpan={9} className="px-4 py-4 text-gray-500">No site matches the filters.</td></tr>}
              {sites.map((s) => (
                <React.Fragment key={s.id}>
                  <tr className={`border-t border-gray-100 hover:bg-gray-50 cursor-pointer ${s.retiredAt ? "opacity-60" : ""}`}
                    onClick={() => setOpenSite(openSite === s.id ? null : s.id)} data-testid={`nerc-site-${s.id}`} aria-expanded={openSite === s.id}>
                    <td className="px-4 py-2 font-medium text-gray-900">
                      {s.name}
                      {s.isCip014CriticalFacility && <span className="ml-2 text-[10px] uppercase px-1 rounded border border-purple-200 bg-purple-50 text-purple-700">CIP-014</span>}
                      {s.retiredAt && <span className="ml-1 text-[10px] uppercase text-gray-400" title={s.retiredReason || ""}>retired</span>}
                      {s.location && <span className="block text-xs text-gray-500 font-normal">{s.location}</span>}
                    </td>
                    <td>{siteTypeLabel(s.siteType)}</td>
                    <td>{s.impact
                      ? <span className={`px-1.5 py-0.5 rounded border text-[11px] font-semibold ${IMPACT_TONES[s.impact]}`}>{s.impact}</span>
                      : <span className="text-xs text-gray-400">Not categorised</span>}</td>
                    <td>{s.highSystems} / {s.mediumSystems} / {s.lowSystems}</td>
                    <td>{s.cyberAssets}</td>
                    <td className={s.needsEsp ? "text-red-700 font-semibold" : "text-gray-400"}>{s.needsEsp}</td>
                    <td>{s.ownerName || "—"}</td>
                    <td className="text-xs text-gray-500">{formatUtcDateTime(s.updatedAt)}</td>
                    <td className="px-3 text-right whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      {canManage && !s.retiredAt && (
                        <>
                          <button type="button" className={`${btn("secondary", "xs")} mr-2`} onClick={() => setDialog({ kind: "site", item: s })}>Edit</button>
                          <button type="button" className={btn("danger", "xs")} onClick={() => setDialog({ kind: "retire", item: s })}>Retire</button>
                        </>
                      )}
                    </td>
                  </tr>
                  {openSite === s.id && (
                    <tr><td colSpan={9} className="p-0">
                      <NercSiteDetails siteId={s.id} canManage={canManage && !s.retiredAt} includeRetired={includeRetired} users={users} onChanged={load} />
                    </td></tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {dialog?.kind === "site" && <SiteForm site={dialog.item} users={users} onClose={() => setDialog(null)} onSaved={saved} />}
      {dialog?.kind === "retire" && <RetireForm kind="site" item={dialog.item} onClose={() => setDialog(null)} onSaved={saved} />}
      {dialog?.kind === "import" && (
        <NercCsvImportModal onClose={() => setDialog(null)}
          onImported={(r) => { setNotice(`Imported: ${r.creates} created, ${r.updates} updated, ${r.unchanged} unchanged.`); saved(); }} />
      )}
    </div>
  );
}
